import { Card, EmptyState } from '@shopify/polaris';
import { useCurrentApp } from '@/stores/userDataStore';
import VideoCard from '@/components/videos/VideoCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import { useQueryStudioVideos } from './hooks/useStudioVideos';
import { useCallback, useMemo, memo } from 'react';
import { TemplateFeatures, TemplateName } from '../studio-video-templates/types/template';
import { TEMPLATES } from '../studio-video-templates/constants/template';
import { Product } from '@/types/product';
import { StudioVideo, StudioVideoStatus } from '../ai-video-creator/stores/studioVideoStore';
import EmptyGrid from '@/components/EmptyGrid/EmptyGrid';
import { TEMPLATES_SHOWING_MEDIA_ASSETS } from '@/features/my-creations-library/hooks/useAllAiCreations';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import { AssetCreationToolName, StandaloneMediaAsset } from '@/types/media';
import { AICreationType } from '@/api/list-ai-creations';
import { useAllAiCreations } from '@/features/my-creations-library/hooks/useAllAiCreations';

type StudioVideosProps = {
  creationTemplateFilter?: TemplateName | 'all';
  selectedFeatures?: Set<TemplateFeatures>;
  selectedProduct?: Product | null;
};

type MediaAssetWrapper = {
  id: string;
  isMediaAsset: true;
  item: StandaloneMediaAsset;
  createdAt: string;
};

type GridItem = StudioVideo | MediaAssetWrapper;

export const StudioVideos = memo(({ creationTemplateFilter, selectedFeatures, selectedProduct }: StudioVideosProps) => {
  const { appKey } = useCurrentApp();
  const { studioVideos, isLoading: isLoadingStudioVideos } = useQueryStudioVideos();
  const { aiCreations, isLoading: isLoadingAiCreations } = useAllAiCreations({ appKey });

  const templateMediaAssets = useMemo(() => {
    if (!aiCreations) return [];

    return aiCreations
      .filter((creation) => {
        if (creation.type !== AICreationType.mediaAsset) return false;

        const { item } = creation;

        if (item.createdWith.toolName !== AssetCreationToolName.studioVideoComposition) return false;

        if (!TEMPLATES_SHOWING_MEDIA_ASSETS.includes(item.metadata?.sourceTemplate as TemplateName)) return false;

        const templateMatch =
          creationTemplateFilter === 'all' || item?.metadata?.sourceTemplate === creationTemplateFilter;
        if (!templateMatch) return false;

        if (selectedProduct && item.productId) {
          return item.productId === selectedProduct.productId;
        } else if (selectedProduct) {
          return false;
        }

        return true;
      })
      .map((creation) => creation.item);
  }, [aiCreations, selectedProduct, creationTemplateFilter]) as StandaloneMediaAsset[];

  const studioVideoIdsToHide = useMemo(() => {
    return new Set(templateMediaAssets.map((asset) => asset.metadata?.sourceStudioVideoId as string).filter(Boolean));
  }, [templateMediaAssets]);

  const filteredStudioVideos = useMemo(() => {
    if (!studioVideos) return [];

    return studioVideos.filter((video) => {
      if (video.status === StudioVideoStatus.DRAFT) return false;

      if (video.id && studioVideoIdsToHide.has(video.id)) return false;

      if (selectedProduct) {
        if (!video.product) return false;
        const productMatch = video.product.productId === selectedProduct.productId;
        if (!productMatch) return false;
      }

      const templateMatch = creationTemplateFilter === 'all' || video.template === creationTemplateFilter;
      if (!templateMatch) return false;

      return true;
    });
  }, [studioVideos, creationTemplateFilter, selectedProduct, studioVideoIdsToHide]);

  const combinedItems = useMemo(() => {
    const studioVideoItems = filteredStudioVideos.map((video) => ({
      ...video,
      createdAt: video.createdAt || new Date().toISOString(),
    }));

    const mediaAssetItems = templateMediaAssets.map((mediaAsset) => ({
      id: mediaAsset.id,
      isMediaAsset: true,
      item: mediaAsset,
      createdAt: mediaAsset.createdAt,
    })) as MediaAssetWrapper[];

    const items: GridItem[] = [...studioVideoItems, ...mediaAssetItems];

    return items.sort((a, b) => {
      const dateA = new Date(a.createdAt || '').getTime();
      const dateB = new Date(b.createdAt || '').getTime();
      return dateB - dateA;
    });
  }, [filteredStudioVideos, templateMediaAssets]);

  const renderCombinedItem = useCallback((item: GridItem) => {
    if ('isMediaAsset' in item) {
      return <MediaAssetCard key={item.id} media={item.item} inGrid />;
    }
    return <VideoCard key={item.id} video={item} options={{ preload: 'none' }} />;
  }, []);

  const isLoading = isLoadingStudioVideos || isLoadingAiCreations;

  if (isLoading || !appKey) {
    return <EmptyGrid staticHeight="calc(100vh - 270px)" />;
  }

  if (!combinedItems?.length) {
    if (!selectedProduct) {
      return (
        <Card>
          <EmptyState
            heading="No content available"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          />
        </Card>
      );
    }

    return (
      <Card>
        <EmptyState
          heading={`No videos found for this product`}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        />
      </Card>
    );
  }

  return <MediaGrid items={combinedItems} renderItem={renderCombinedItem} staticHeight="calc(100vh - 270px)" />;
});

StudioVideos.displayName = 'StudioVideos';
